/**
 * A parte da área de administração que não desenha nada (docs/spec/admin.md): as ações que cada
 * conta oferece, com o motivo de as que não valem, e a senha provisória.
 */
import { adminRefusal, parseAdminPlanConfigDetail, parseAdminPlanDetail, parseAdminPluginLogDetail, parseAdminRoomDeleteDetail, type AdminLogAction, type AdminLogEntry, type AdminRefusal, type AdminUserRow, type EntitlementKey } from '../shared.js';
import { ENTITLEMENT_KEYS, entitlementValue, planName, priceText } from '../plans/planModel.js';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** As seções da Administração, na ordem da lista da esquerda. */
export type AdminPage = 'overview' | 'users' | 'plans' | 'rooms' | 'plugins' | 'server' | 'log';
export const ADMIN_PAGES: readonly AdminPage[] = ['overview', 'users', 'plans', 'rooms', 'plugins', 'server', 'log'];

/** Uma ação do menu ou do painel; `refusal`: por que não vale (o botão fica desligado, com a dica). */
export type AdminActionOption = { action: AdminLogAction; refusal: AdminRefusal | null };

/** As ações da conta, na ordem da tela: suspender (ou reativar), senha, plano, papel de admin e apagar. */
export function availableActions(row: Pick<AdminUserRow, 'user' | 'admin' | 'suspension'>, meId: string): AdminActionOption[] {
  const target = { id: row.user.id, admin: row.admin, suspended: row.suspension !== null };
  const actions: AdminLogAction[] = [row.suspension ? 'unsuspend' : 'suspend', 'password', 'plan', row.admin ? 'demote' : 'promote', 'delete'];
  return actions.map((action) => ({ action, refusal: adminRefusal(meId, target, action) }));
}

/** Letras e números que não se confundem ao ditar ou copiar à mão (sem 0/O, 1/l/I). */
const PASSWORD_ALPHABET = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * Uma senha provisória para o admin passar à pessoa: 12 caracteres sorteados em três grupos
 * ("k7Pq-x2Mf-9cRt"), fácil de ditar e bem acima do mínimo de 8.
 */
export function generatePassword(randomBytes: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  const bytes = randomBytes(12);
  // 256 não é múltiplo de 55: o resto da divisão favorece um pouco as primeiras letras, o que não
  // importa numa senha que a pessoa troca logo
  const chars = [...bytes].map((byte) => PASSWORD_ALPHABET[byte % PASSWORD_ALPHABET.length]!);
  return [chars.slice(0, 4), chars.slice(4, 8), chars.slice(8)].map((group) => group.join('')).join('-');
}

/**
 * O detalhe de uma linha do registro, em texto: o motivo da suspensão como veio, e a troca de plano
 * (docs/spec/planos.md) como "Grátis → Plano 2 · até 23/01/2027 · Pix de 23/10". Um `detail` de plano
 * que não for o JSON esperado aparece cru.
 */
export function logDetailText(
  t: Translate,
  entry: Pick<AdminLogEntry, 'action' | 'detail'>,
  date: (ms: number) => string,
  name: (planId: string) => string = (planId) => planName(t, planId),
  language: 'pt-BR' | 'en' = 'pt-BR',
): string {
  if (entry.action === 'plan-config') return planConfigText(t, entry.detail, language);
  if (entry.action === 'room-delete') {
    const room = parseAdminRoomDeleteDetail(entry.detail);
    return room ? t('admin.roomDelete.detail', { name: room.roomName }) : entry.detail;
  }
  if (isPluginAction(entry.action)) return pluginLogText(t, entry.action, entry.detail);
  if (entry.action !== 'plan') return entry.detail;
  const plan = parseAdminPlanDetail(entry.detail);
  if (!plan) return entry.detail;
  const parts = [`${name(plan.from)} → ${name(plan.to)}`];
  if (plan.expiresAt !== null) parts.push(t('admin.plan.until', { date: date(plan.expiresAt) }));
  if (plan.note) parts.push(plan.note);
  return parts.join(' · ');
}

/**
 * O que uma edição de plano mudou (docs/spec/planos.md, "Editar os planos"): "Espaço 5 GB → 10 GB ·
 * Preço R$ 5,00 → R$ 7,00", ou "Voltou ao padrão". Um `detail` que não for o JSON esperado aparece cru.
 */
function planConfigText(t: Translate, raw: string, language: 'pt-BR' | 'en'): string {
  const detail = parseAdminPlanConfigDetail(raw);
  if (!detail) return raw;
  if (detail.reset) return t('admin.planConfig.reset');
  const parts: string[] = [];
  const value = (key: EntitlementKey, v: unknown) => (typeof v === 'number' || typeof v === 'boolean' || v === null ? entitlementValue(t, key, v) : '—');
  for (const key of ENTITLEMENT_KEYS) {
    const before = detail.before.entitlements[key];
    const after = detail.after.entitlements[key];
    if (before !== after) parts.push(`${t(`plans.entitlement.${key}`)} ${value(key, before)} → ${value(key, after)}`);
  }
  for (const lang of ['pt', 'en'] as const) {
    const before = detail.before.names[lang] ?? '';
    const after = detail.after.names[lang] ?? '';
    if (before !== after) parts.push(`${t(`admin.planConfig.name.${lang}`)} ${before || '—'} → ${after || '—'}`);
  }
  if (detail.before.priceCents !== detail.after.priceCents) {
    parts.push(`${t('admin.planConfig.price')} ${priceText(t, detail.before.priceCents, language)} → ${priceText(t, detail.after.priceCents, language)}`);
  }
  return parts.join(' · ');
}

/** As ações sobre um plugin oficial: a linha do registro não tem conta como alvo, e sim o plugin. */
export const isPluginAction = (action: AdminLogAction): boolean => action.startsWith('plugin-');

/**
 * O detalhe de uma ação sobre um plugin oficial: "1.0.0 → 1.1.0" na versão nova, a versão e os
 * sistemas ao publicar ("1.0.0 · Sistemas: D&D 5e, 5e"), os sistemas novos, e a versão nas outras.
 */
function pluginLogText(t: Translate, action: AdminLogAction, raw: string): string {
  const detail = parseAdminPluginLogDetail(raw);
  if (!detail) return raw;
  const systems = detail.systems?.length ? t('admin.plugins.logSystems', { systems: detail.systems.join(', ') }) : t('admin.plugins.logNoSystems');
  if (action === 'plugin-version' && detail.from) return `${detail.from} → ${detail.version}`;
  if (action === 'plugin-add') return `${detail.version} · ${systems}`;
  if (action === 'plugin-systems') return systems;
  return detail.version;
}
